import type { ReactNode } from 'react'
import { Clock, MessageSquarePlus, Settings } from 'lucide-react'
import { cn } from '../lib/cn'

export type Tab = 'chat' | 'prompts' | 'documents'

export interface TabBarProps {
  tab: Tab
  onTab: (tab: Tab) => void
  onHistory?: () => void
  onNewChat?: () => void
  onSettings?: () => void
  /** Replaces the default chat toolbar icons (e.g. a "+ Neu" button per tab). */
  actions?: ReactNode
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'chat', label: 'Chat' },
  { id: 'prompts', label: 'Prompts' },
  { id: 'documents', label: 'Dokumente' },
]

export function TabBar({ tab, onTab, onHistory, onNewChat, onSettings, actions }: TabBarProps) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-200 px-4">
      <nav className="flex gap-5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onTab(t.id)}
            className={cn(
              '-mb-px border-b-2 py-3 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-neutral-500 hover:text-neutral-800',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <div className="flex items-center gap-1.5 py-2">
        {actions ?? (
          <>
            <ToolbarButton label="Verlauf" onClick={onHistory}>
              <Clock className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Neuer Chat" onClick={onNewChat}>
              <MessageSquarePlus className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Einstellungen" onClick={onSettings}>
              <Settings className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}
      </div>
    </div>
  )
}

function ToolbarButton({
  children,
  label,
  onClick,
}: {
  children: ReactNode
  label: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50"
    >
      {children}
    </button>
  )
}
